import {
  breakdownOptions,
  defaultReportView,
  kpiDefinitions,
} from "./defaults";
import { BreakdownKey, KpiKey, ReportView } from "./types";

const storageKey = (workspaceUuid: string) =>
  `finance-reporting-view:${workspaceUuid}`;

const freshDefaultView = (): ReportView => ({
  ...defaultReportView,
  periods: [...defaultReportView.periods],
  filters: {
    client: [],
    manager: [],
    campaignName: [],
    creatorName: [],
    market: [],
    month: [],
    customDimension1: [],
    customDimension2: [],
  },
  kpis: [...defaultReportView.kpis],
});

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

/**
 * Every possible `BreakdownKey`, standard or custom. A workspace's additional dimensions
 * are configuration, not part of this static list, so a saved view is validated and
 * rehydrated against the full key set regardless of what the workspace currently has
 * configured - otherwise a stored filter for a custom dimension would be silently dropped
 * on reload, and `view.filters[key]` would be undefined wherever a reader does not itself
 * guard with `?? []`.
 */
const ALL_BREAKDOWN_KEYS: { key: BreakdownKey }[] = [
  ...breakdownOptions,
  { key: "customDimension1" },
  { key: "customDimension2" },
];

export function loadReportView(workspaceUuid: string): ReportView {
  const fallback = freshDefaultView();

  try {
    const stored = window.localStorage.getItem(storageKey(workspaceUuid));
    if (!stored) return fallback;

    const value = JSON.parse(stored) as Partial<ReportView>;
    const breakdown = ALL_BREAKDOWN_KEYS.some(
      ({ key }) => key === value.breakdown,
    )
      ? (value.breakdown as BreakdownKey)
      : fallback.breakdown;
    const allowedKpis = new Set<KpiKey>(kpiDefinitions.map(({ key }) => key));
    const kpis = isStringArray(value.kpis)
      ? value.kpis.filter((key): key is KpiKey =>
          allowedKpis.has(key as KpiKey),
        )
      : fallback.kpis;
    const filters = Object.fromEntries(
      ALL_BREAKDOWN_KEYS.map(({ key }) => {
        const selected = value.filters?.[key];
        return [key, isStringArray(selected) ? selected : []];
      }),
    ) as ReportView["filters"];

    return {
      periods: isStringArray(value.periods) ? value.periods : fallback.periods,
      breakdown,
      filters,
      kpis: kpis.length ? kpis : fallback.kpis,
    };
  } catch (error) {
    console.warn("Could not read the saved Finance Reporting view", error);
    return fallback;
  }
}

export function saveReportView(workspaceUuid: string, view: ReportView): void {
  try {
    window.localStorage.setItem(
      storageKey(workspaceUuid),
      JSON.stringify(view),
    );
  } catch (error) {
    console.warn("Could not save the Finance Reporting view", error);
  }
}
